<!--
  The sidebar's swiftChat / Chat buttons, on every page.

  The three role dashboards (Originate, Process, LO assistant) dock these chats as side panels and
  answer the buttons themselves (helpers/sidebarChatBridge.js). Everywhere else — the other Command
  Center views (Opportunity, Underwrite, Insights, Pipeline Board) and every other page — nothing
  answered, and the button only sent you back to "/". This host is mounted once in App.vue and opens
  the same drawers right where you are whenever no dashboard dock is on screen (chatLauncher.js
  hasChatHost()).
-->
<template>
  <div class="sidebar-chat-host">
    <!-- AIFlyoutDrawer fills the box it is given (the dashboards give it their dock); this is ours. -->
    <transition name="sch-drawer">
      <div v-if="isAIDrawerOpen" class="sch-overlay" @click.self="isAIDrawerOpen = false">
        <div class="sch-panel sch-panel--ai" role="dialog" aria-label="swiftChat" :style="{ width: aiWidth + 'px' }">
          <div class="sch-resize-handle" title="Drag to resize" @pointerdown="onResizeStarted($event, 'ai')"></div>
          <div v-if="resizing" class="sch-drag-shield"></div>
          <AIFlyoutDrawer :open="true"
                          :docked="true"
                          :tabs="AITabs"
                          :selected-tab="selectedTab"
                          :uw-guides-url="aiUrl('/chat/gpt/iframe/')"
                          :doc-analysis-url="aiUrl('/chat/summary')"
                          :pipeline-url="aiUrl('/chat/gpt/iframe/pipeline')"
                          :productFinder-url="aiUrl('/iframe/productFinder')"
                          :azure-id="azureId"
                          :user-name="userName"
                          @close="isAIDrawerOpen = false"
                          @tab-selected="onTabSelected" />
        </div>
      </div>
    </transition>

    <transition name="sch-drawer">
      <div v-if="isLoanLaunchDrawerOpen" class="sch-overlay" @click.self="isLoanLaunchDrawerOpen = false">
        <div class="sch-panel" role="dialog" aria-label="LoanLaunch Chat" :style="{ width: loanLaunchWidth + 'px' }">
          <div class="sch-resize-handle" title="Drag to resize" @pointerdown="onResizeStarted($event, 'loanLaunch')"></div>
          <div v-if="resizing" class="sch-drag-shield"></div>
          <LoanLaunchMessagesDrawer @close="isLoanLaunchDrawerOpen = false" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import bus from "@/helpers/appBus.js"
import auth from "@/authConfig.js"
import { CHAT_OPEN_EVENT, CHAT_KIND_SWIFT, CHAT_KIND_LOANLAUNCH, hasChatHost } from "@/helpers/chatLauncher.js"
import AIFlyoutDrawer from "@/components/commandCenter/AIFlyoutDrawer.vue"
import LoanLaunchMessagesDrawer from "@/components/commandCenter/LoanLaunchMessagesDrawer.vue"

// Drag the left edge to resize. Same keys, minimum and default as the dashboards' docks
// (LoanOfficerDashboard.vue _initDockWidths / stopResize), so a width set in one place is the
// width everywhere.
const WIDTH_KEYS = { ai: "cc_ai_dock_width", loanLaunch: "cc_loanlaunch_dock_width" }
const MIN_WIDTH = 740
const VIEWPORT_MARGIN = 24

function readWidth(key) {
  try {
    const n = parseInt(localStorage.getItem(key), 10)

    return isNaN(n) ? null : n
  } catch (e) {
    return null
  }
}

function saveWidth(key, width) {
  try {
    localStorage.setItem(key, String(Math.round(width)))
  } catch (e) {
    // Private window / storage blocked: the width just isn't remembered.
  }
}

export default {
  name: "SidebarChatHost",
  components: { AIFlyoutDrawer, LoanLaunchMessagesDrawer },
  data() {
    return {
      // Same tabs as the dashboards' swiftChat dock (LoanOfficerDashboard.vue AITabs).
      aiWidth: MIN_WIDTH,
      AITabs: [
        { title: "Search UW Guidelines", content: "UWGuides" },
        { title: "Document Analyzer", content: "docsum" },
        { title: "Pipeline Insights", content: "pipeline" },
        { title: "Product Finder", content: "product_finder" }
      ],
      azureId: "",
      isAIDrawerOpen: false,
      isLoanLaunchDrawerOpen: false,
      loanLaunchWidth: MIN_WIDTH,
      resizing: null, // "ai" | "loanLaunch" while the edge is being dragged
      selectedTab: 0,
      userName: ""
    }
  },
  mounted() {
    bus.on(CHAT_OPEN_EVENT, this.onChatOpen)
    window.addEventListener("resize", this.onWindowResized)
  },
  beforeUnmount() {
    bus.off(CHAT_OPEN_EVENT, this.onChatOpen)
    window.removeEventListener("resize", this.onWindowResized)
    this.onResizeEnded()
  },
  methods: {
    // Same hosts the dashboards use: dev AI for localhost / dev.*, production otherwise.
    aiUrl(path) {
      const hostname = typeof window !== "undefined" ? window.location.hostname : ""
      const base = hostname.startsWith("dev.") || hostname === "localhost"
        ? "https://devai.goldstarfinancial.com"
        : "https://ai.swiftapp.ai"

      return `${base}${path}`
    },
    // Saved width, or 63% of the window like the dashboards; never under the minimum or wider than
    // the window allows.
    clampWidth(width) {
      const max = Math.max(320, window.innerWidth - VIEWPORT_MARGIN)

      return Math.min(max, Math.max(Math.min(MIN_WIDTH, max), width))
    },
    initialWidth(which) {
      const saved = readWidth(WIDTH_KEYS[which])

      return this.clampWidth(saved ?? Math.round(window.innerWidth * 0.63))
    },
    async loadUser() {
      if (this.azureId && this.userName) return

      this.azureId = localStorage.getItem("azure_id") || ""
      this.userName = localStorage.getItem("user_name") || ""
      if (this.userName) return

      try {
        const token = await auth.acquireTokenResponse()
        this.userName = token?.account?.name || ""
      } catch (e) {
        // The drawer still opens; the AI pages only use the name for a greeting.
      }
    },
    async onChatOpen(kind) {
      // A dashboard with its own docked chat is on screen: it answers the button.
      if (hasChatHost()) return

      if (kind === CHAT_KIND_SWIFT) {
        await this.loadUser()
        this.aiWidth = this.initialWidth("ai")
        this.isLoanLaunchDrawerOpen = false
        this.isAIDrawerOpen = true
      } else if (kind === CHAT_KIND_LOANLAUNCH) {
        this.loanLaunchWidth = this.initialWidth("loanLaunch")
        this.isAIDrawerOpen = false
        this.isLoanLaunchDrawerOpen = true
      }
    },
    onResizeEnded() {
      if (!this.resizing) return

      const which = this.resizing

      this.resizing = null
      document.body.classList.remove("sch-grabbing")
      window.removeEventListener("pointermove", this.onResizeMoved)
      window.removeEventListener("pointerup", this.onResizeEnded)
      window.removeEventListener("pointercancel", this.onResizeEnded)
      saveWidth(WIDTH_KEYS[which], which === "ai" ? this.aiWidth : this.loanLaunchWidth)
    },
    onResizeMoved(evt) {
      if (!this.resizing) return

      evt.preventDefault()
      const width = this.clampWidth(window.innerWidth - evt.clientX)

      if (this.resizing === "ai") this.aiWidth = width
      else this.loanLaunchWidth = width
    },
    onResizeStarted(evt, which) {
      this.resizing = which
      try { evt.target.setPointerCapture?.(evt.pointerId) } catch (e) { /* not needed for the drag */ }
      document.body.classList.add("sch-grabbing")
      window.addEventListener("pointermove", this.onResizeMoved, { passive: false })
      window.addEventListener("pointerup", this.onResizeEnded, { passive: true })
      window.addEventListener("pointercancel", this.onResizeEnded, { passive: true })
    },
    onWindowResized() {
      this.aiWidth = this.clampWidth(this.aiWidth)
      this.loanLaunchWidth = this.clampWidth(this.loanLaunchWidth)
    },
    onTabSelected(selected) {
      this.selectedTab = selected
    }
  }
}
</script>

<style scoped>
/* LoanLaunch chat: same slide-in panel DashboardShell.vue uses for it. */
.sch-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.35);
}

.sch-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 420px;
  max-width: 100vw;
  height: 100%;
  overflow-y: auto;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
}

.sch-panel--ai {
  overflow: hidden;
}

/* Grab strip on the left edge (same as the dashboards' .cc-ai-resize-handle). */
.sch-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  width: 12px;
  height: 100%;
  cursor: ew-resize;
  touch-action: none;
  user-select: none;
}

.sch-resize-handle:hover {
  background: linear-gradient(90deg, rgba(47, 111, 237, 0.18), transparent);
}

/* The messages list caps itself at 40rem for its card use; in the panel it fills the width you drag to. */
.sch-panel :deep(.loanlaunch-messages) {
  max-width: none;
  width: 100%;
}

/* While dragging, cover the chat so its iframes don't swallow the pointer. */
.sch-drag-shield {
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: ew-resize;
}

.sch-drawer-enter-active,
.sch-drawer-leave-active {
  transition: opacity 0.22s ease;
}

.sch-drawer-enter-active .sch-panel,
.sch-drawer-leave-active .sch-panel {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.sch-drawer-enter-from,
.sch-drawer-leave-to {
  opacity: 0;
}

.sch-drawer-enter-from .sch-panel,
.sch-drawer-leave-to .sch-panel {
  transform: translateX(100%);
}
</style>

<style>
/* Unscoped: on <body> while dragging, so the cursor stays and text doesn't get selected. */
body.sch-grabbing,
body.sch-grabbing * {
  cursor: ew-resize !important;
  user-select: none !important;
}
</style>
